import { useState } from "react";
import {
  Campaign,
  Event,
  MassText,
  MassTextAttachment,
  OnboardingFlowType,
  ScheduleTextInfo,
  SubSelectionItem,
  SubSelectionType,
} from "@markit/common.types";
import {
  MassTextMessagingScreenType,
  MassTextScreenType,
} from "../MassTextPanel";
import { MassTextMessageDrafting } from "../Message/MassTextMessageDrafting";
import { ThemeStyle } from "../../../../hooks/useTheme";
import MassTextsSendDetails from "./MassTextsSendDetails";
import OnboardingTourStep from "../../../Onboarding/OnboardingTourStep";

export const SEND_FIRST_TEXT_TOTAL_STEPS = 5;

export type MassTextMessageCompositionProps = {
  campaign: Campaign | undefined;
  finalRecipients: string[];
  netIndividualRecipients: string[];
  massText: MassText;
  updateMassTextSettings: (massTextSettings: Partial<MassText>) => void;
  scheduleTextInfo: ScheduleTextInfo;
  setScheduleTextInfo: (scheduleTextInfo: ScheduleTextInfo) => void;
  numCredits: number;
  numFollowersNotCompliant: number;
  showComplianceCreditNotice: boolean;
  convertedMessage: string;
  finalMessageWithAppendages: string;
  massTextAttachments: MassTextAttachment[] | undefined;
  setMainScreen: (mainScreen: MassTextScreenType) => void;
  screen: MassTextMessagingScreenType;
  setScreen: (screen: MassTextMessagingScreenType) => void;
  isEditing: boolean;
  suggestedAction: SubSelectionItem | undefined;
  loadingRecipients: boolean;
  event?: Event;
  theme?: ThemeStyle;
};

const MassTextMessageComposition = (props: MassTextMessageCompositionProps) => {
  const {
    campaign,
    finalRecipients,
    netIndividualRecipients,
    massText,
    updateMassTextSettings,
    scheduleTextInfo,
    setScheduleTextInfo,
    numCredits,
    numFollowersNotCompliant,
    showComplianceCreditNotice,
    convertedMessage,
    finalMessageWithAppendages,
    massTextAttachments,
    setMainScreen,
    screen,
    setScreen,
    isEditing,
    suggestedAction,
    loadingRecipients,
    event,
    theme,
  } = props;
  const [sendFirstTextTourStep, setSendFirstTextTourStep] = useState(1);

  return (
    <div
      className="ColumnNormal onboarding-send-first-text-intro-target"
      style={{ height: "100%" }}
    >
      <MassTextsSendDetails
        massText={massText}
        updateMassTextSettings={updateMassTextSettings}
        scheduleTextInfo={scheduleTextInfo}
        setScheduleTextInfo={setScheduleTextInfo}
        setMainScreen={setMainScreen}
        screen={screen}
        setScreen={setScreen}
        netRecipients={finalRecipients.length}
        suggestedAction={suggestedAction}
        loadingRecipients={loadingRecipients}
        event={event}
        isEditing={isEditing}
      />
      <MassTextMessageDrafting
        campaign={campaign}
        massText={massText}
        updateMassTextSettings={updateMassTextSettings}
        numCredits={numCredits}
        numFollowersNotCompliant={numFollowersNotCompliant}
        showComplianceCreditNotice={showComplianceCreditNotice}
        convertedMessage={convertedMessage}
        finalMessageWithAppendages={finalMessageWithAppendages}
        disableEventLinks={
          netIndividualRecipients.length > 0 ||
          suggestedAction?.type === SubSelectionType.ALL_FOLLOWERS
        }
        massTextAttachments={massTextAttachments}
        event={event}
        theme={theme}
      />
      {!isEditing ? (
        sendFirstTextTourStep === 1 && !loadingRecipients ? (
          <OnboardingTourStep
            key="send-first-text-tour-step-1"
            flowType={OnboardingFlowType.SEND_FIRST_TEXT}
            target=".onboarding-send-first-text-intro-target"
            placement="left"
            content={
              <>
                This is where you draft and send your texts. You can select your
                recipients and choose to send the text immediately or scheduled
                at a later time.
                <br />
                <br />
                When drafting, type &quot;/&quot; to see the text plugin options
                that dynamically fill in at send time.
              </>
            }
            stepNumber={1}
            totalSteps={SEND_FIRST_TEXT_TOTAL_STEPS}
            onStepComplete={() => setSendFirstTextTourStep(2)}
          />
        ) : sendFirstTextTourStep === 2 ? (
          <OnboardingTourStep
            key="send-first-text-tour-step-2"
            flowType={OnboardingFlowType.SEND_FIRST_TEXT}
            target=".onboarding-send-first-text-credits-target"
            content={
              <>
                Here is the breakdown to show how many credits the text will be
                based on the number of characters, special characters, and
                media.
                <br />
                <br />
                The projected send rate is also shown here, which may lower
                based on the message content.
              </>
            }
            stepNumber={2}
            totalSteps={SEND_FIRST_TEXT_TOTAL_STEPS}
            placement="right"
            onStepComplete={() => setSendFirstTextTourStep(3)}
          />
        ) : sendFirstTextTourStep === 3 ? (
          <OnboardingTourStep
            key="send-first-text-tour-step-3"
            flowType={OnboardingFlowType.SEND_FIRST_TEXT}
            target=".onboarding-send-first-text-attach-target"
            content={
              <>
                Click the &quot;+&quot; or media icon to add attachments or an
                image to your text.
              </>
            }
            stepNumber={3}
            totalSteps={SEND_FIRST_TEXT_TOTAL_STEPS}
            placement="left"
            onStepComplete={() => setSendFirstTextTourStep(4)}
          />
        ) : sendFirstTextTourStep === 4 ? (
          <OnboardingTourStep
            key="send-first-text-tour-step-4"
            flowType={OnboardingFlowType.SEND_FIRST_TEXT}
            target=".onboarding-send-first-text-recipients-target"
            content={
              <>
                Click &quot;Select Recipients&quot; to add who you want to send
                your text to!
              </>
            }
            stepNumber={4}
            totalSteps={SEND_FIRST_TEXT_TOTAL_STEPS}
            placement="bottom"
          />
        ) : null
      ) : null}
    </div>
  );
};

export default MassTextMessageComposition;
